import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, User, Phone, Mail, Calendar, ChevronRight, ArrowUpDown, Users as UsersIcon, Briefcase, PoundSterling } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AdminLayout from '../components/AdminLayout';
import { formatUkDate } from '../lib/date';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORTS = {
  name: { label: 'Name (A–Z)', fn: (a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`) },
  recent: { label: 'Most Recent Trip', fn: (a, b) => String(b.lastPickupDate || '').localeCompare(String(a.lastPickupDate || '')) },
  spend: { label: 'Highest Spend', fn: (a, b) => Number(b.totalSpend || 0) - Number(a.totalSpend || 0) },
  jobs: { label: 'Most Bookings', fn: (a, b) => Number(b.totalBookings || 0) - Number(a.totalBookings || 0) },
};

const Customers = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('recent');

  const fetchCustomers = async (q = '') => {
    setLoading(true);
    try {
      const params = q ? `?q=${encodeURIComponent(q)}` : '';
      const { data } = await axios.get(`${API}/admin/customers${params}`);
      setCustomers(data.customers || []);
    } catch (e) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers('');
  }, []);

  // Debounced server-side search
  useEffect(() => {
    const t = setTimeout(() => fetchCustomers(query.trim()), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const sorted = useMemo(() => {
    const arr = [...customers];
    arr.sort(SORTS[sortKey].fn);
    return arr;
  }, [customers, sortKey]);

  const stats = useMemo(() => {
    const total = customers.length;
    const totalSpend = customers.reduce((acc, c) => acc + Number(c.totalSpend || 0), 0);
    const totalJobs = customers.reduce((acc, c) => acc + Number(c.totalBookings || 0), 0);
    return { total, totalSpend, totalJobs };
  }, [customers]);

  const initials = (c) => `${c.firstName?.[0] || '?'}${c.lastName?.[0] || ''}`.toUpperCase();

  return (
    <AdminLayout title="Customers" subtitle="All passengers who have booked with you">
      <div className="space-y-5">
        {/* Stat strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card className="p-4 flex items-center gap-3" data-testid="customer-stat-total">
            <div className="h-9 w-9 rounded-lg bg-black text-white flex items-center justify-center">
              <UsersIcon className="h-4 w-4" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-500">Customers</p>
              <p className="text-xl font-bold text-black leading-tight">{stats.total}</p>
            </div>
          </Card>
          <Card className="p-4 flex items-center gap-3" data-testid="customer-stat-jobs">
            <div className="h-9 w-9 rounded-lg bg-black text-white flex items-center justify-center">
              <Briefcase className="h-4 w-4" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-500">Total Jobs</p>
              <p className="text-xl font-bold text-black leading-tight">{stats.totalJobs}</p>
            </div>
          </Card>
          <Card className="p-4 flex items-center gap-3" data-testid="customer-stat-spend">
            <div className="h-9 w-9 rounded-lg bg-black text-white flex items-center justify-center">
              <PoundSterling className="h-4 w-4" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-500">Lifetime Value</p>
              <p className="text-xl font-bold text-black leading-tight">£{stats.totalSpend.toFixed(2)}</p>
            </div>
          </Card>
        </div>

        {/* Search + sort */}
        <Card className="p-3 sm:p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email or phone…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-10 bg-white h-10"
                data-testid="customers-search-input"
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <ArrowUpDown className="h-4 w-4 text-gray-500" />
              <Select value={sortKey} onValueChange={setSortKey}>
                <SelectTrigger className="w-48 bg-white h-10" data-testid="customers-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SORTS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Table */}
        {loading ? (
          <Card className="p-6 text-sm text-gray-600">Loading…</Card>
        ) : sorted.length === 0 ? (
          <Card className="p-10 text-center">
            <User className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-700 font-medium">No customers match your search.</p>
          </Card>
        ) : (
          <Card className="overflow-hidden p-0" data-testid="customers-list">
            {/* Header row — hidden on small screens */}
            <div className="hidden md:grid grid-cols-[1fr,1.2fr,0.7fr,0.7fr,0.9fr,32px] gap-4 px-5 py-3 bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wide font-semibold text-gray-500">
              <div>Customer</div>
              <div>Contact</div>
              <div className="text-right">Jobs</div>
              <div className="text-right">Spend</div>
              <div>Last Trip</div>
              <div />
            </div>

            <ul className="divide-y divide-gray-100">
              {sorted.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/customers/${encodeURIComponent(c.id)}`)}
                    className="w-full text-left px-5 py-3 grid grid-cols-1 md:grid-cols-[1fr,1.2fr,0.7fr,0.7fr,0.9fr,32px] gap-3 md:gap-4 items-center hover:bg-gray-50 transition"
                    data-testid={`customer-row-${c.id}`}
                  >
                    {/* Avatar + name */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-9 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold shrink-0">
                        {initials(c)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-black truncate">{c.firstName} {c.lastName}</p>
                        <p className="md:hidden text-xs text-gray-500 truncate">{c.email}</p>
                      </div>
                    </div>

                    {/* Contact (desktop only) */}
                    <div className="hidden md:block min-w-0 text-sm text-gray-600">
                      <p className="truncate flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-gray-400" /> {c.email}</p>
                      {c.phone && (
                        <p className="truncate flex items-center gap-1.5 mt-0.5"><Phone className="h-3.5 w-3.5 text-gray-400" /> {c.phone}</p>
                      )}
                    </div>

                    {/* Jobs */}
                    <div className="hidden md:block text-right font-semibold text-black tabular-nums">
                      {c.totalBookings || 0}
                    </div>

                    {/* Spend */}
                    <div className="hidden md:block text-right font-semibold text-black tabular-nums">
                      £{Number(c.totalSpend || 0).toFixed(0)}
                    </div>

                    {/* Last trip */}
                    <div className="hidden md:flex items-center gap-1.5 text-xs text-gray-600">
                      <Calendar className="h-3.5 w-3.5 text-gray-400" />
                      {formatUkDate(c.lastPickupDate) || '—'}
                    </div>

                    {/* Arrow */}
                    <div className="hidden md:flex justify-end">
                      <ChevronRight className="h-4 w-4 text-gray-300" />
                    </div>

                    {/* Mobile compact summary row */}
                    <div className="md:hidden flex items-center gap-4 text-xs text-gray-600 -mt-1">
                      <span><strong className="text-black">{c.totalBookings || 0}</strong> jobs</span>
                      <span><strong className="text-black">£{Number(c.totalSpend || 0).toFixed(0)}</strong> spend</span>
                      <span>{formatUkDate(c.lastPickupDate) || '—'}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default Customers;
